import {
    SUPPORTED_SCHEMA,
    type BankInfo,
    type Meeting,
    type PiprBundle,
} from '../pipr/types';

export interface WireBank {
    bank: string;
    name: string;
    rate_name: string;
    anchor_kind: string;
    decision_tz: string;
    series_slug: string;
}

export interface WireBinLink {
    level_bp: number;
    delta_bp: number;
    condition_id: string;
    label: string;
}

export interface WireMeeting {
    decision_ts: number;
    decided_bp: number | null;
    meeting_date: string;
    kind: string;
    label: string | null;
    event_id: string | null;
    event_slug: string | null;
    bins: WireBinLink[];
    final?: {
        ts: number;
        p: number[];
        implied_bp: number;
        anchor_bp: number;
        pending_bp: number;
    } | null;
}

export interface WireHistory {
    n: number;
    ts: number[];
    anchor_bp: number[];
    pending_bp: number[];
    implied_bp: number[];
    meeting_idx: number[];
    coverage_pct: number[];
    flags: number[];
    p: number[][];
}

export interface WireForward {
    asof_ts: number;
    anchor_bp: number;
    pending_bp: number;
    pending_announced_ts: number | null;
    pending_effective_ts: number | null;
    meetings: Array<{
        decision_ts: number;
        p: number[];
        rate_bp: number;
        base_bp: number;
    }>;
}

export interface WirePiprChart {
    schema_version: number;
    indicator: string;
    bank: string;
    banks: WireBank[];
    methodology_version: string;
    generated_ts: number;
    bucket_sec: number;
    history_start_ts: number;
    first_cut_ts: number;
    last_cut_ts: number;
    fold_bp: number;
    deltas: number[];
    meetings: WireMeeting[];
    history: WireHistory;
    forward: WireForward;
}

export class SchemaError extends Error {
    constructor() {
        super('schema');
        this.name = 'SchemaError';
    }
}

function mapBank(b: WireBank): BankInfo {
    return {
        bank: b.bank,
        name: b.name,
        rateName: b.rate_name,
        anchorKind: b.anchor_kind,
        decisionTz: b.decision_tz,
        seriesSlug: b.series_slug,
    };
}

function mapMeeting(m: WireMeeting): Meeting {
    return {
        decisionTs: m.decision_ts,
        decidedBp: m.decided_bp,
        meetingDate: m.meeting_date,
        label: m.label,
        eventId: m.event_id,
        eventSlug: m.event_slug,
        bins: m.bins.map(b => ({
            levelBp: b.level_bp,
            deltaBp: b.delta_bp,
            conditionId: b.condition_id,
            label: b.label,
        })),
        final: m.final
            ? {
                  ts: m.final.ts,
                  p: m.final.p,
                  impliedBp: m.final.implied_bp,
                  anchorBp: m.final.anchor_bp,
                  pendingBp: m.final.pending_bp,
              }
            : null,
    };
}

export function fromWire(w: WirePiprChart): PiprBundle {
    if (w.schema_version !== SUPPORTED_SCHEMA) throw new SchemaError();
    const h = w.history;
    return {
        schemaVersion: w.schema_version,
        methodologyVersion: w.methodology_version,
        bank: w.bank,
        banks: w.banks.map(mapBank),
        generatedTs: w.generated_ts,
        bucketSec: w.bucket_sec,
        historyStartTs: w.history_start_ts,
        firstCutTs: w.first_cut_ts,
        lastCutTs: w.last_cut_ts,
        meetings: w.meetings.map(mapMeeting),
        history: {
            ts: h.ts,
            n: h.n,
            deltas: w.deltas,
            anchorBp: h.anchor_bp,
            pendingBp: h.pending_bp ?? new Array(h.n).fill(0),
            impliedBp: h.implied_bp,
            p: h.p,
            meetingIdx: h.meeting_idx,
            flags: h.flags,
        },
        forward: {
            asofTs: w.forward.asof_ts,
            anchorBp: w.forward.anchor_bp,
            pendingBp: w.forward.pending_bp ?? 0,
            pendingAnnouncedTs: w.forward.pending_announced_ts ?? null,
            pendingEffectiveTs: w.forward.pending_effective_ts ?? null,
            deltas: w.deltas,
            meetings: w.forward.meetings.map(m => ({
                decisionTs: m.decision_ts,
                p: m.p,
                rateBp: m.rate_bp,
                baseBp: m.base_bp,
            })),
        },
    };
}
